import { CodeInput } from '@components/App/Auth/VerifyEmail/components/CodeInput/CodeInput'
import { ResendCode } from '@components/App/Auth/VerifyEmail/components/ResendCode'
import { useClearRedirectAfterAuth } from '@components/App/Auth/VerifyEmail/hooks/useClearRedirectAfterAuth'
import { MODAL_AUTH } from '@components/App/Modal/constants'
import { Button } from '@octopus-do/octopus-storybook'
import { OctopusPic } from '@octopus-do/octopus-storybook/icons'
import * as ActionTypes from '@storeOld/actions'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useState } from 'react'
import { useDispatch } from 'react-redux'

import API from '@/api'
import { GtmTriggerName, pushPlanTier, sendGtmEvent } from '@/constants/gtm'
import { RequestActions } from '@/store'
import IconLeft from '../../../../lib/icons/IconLeft'
import css from './styles.module.css'

type Props = {
  email: string
  emailVerificationCodeExpiresAt: string
}

export const VerifyEmail: React.FC<Props> = ({
  email,
  emailVerificationCodeExpiresAt,
}) => {
  const dispatch = useDispatch()
  const clearRedirect = useClearRedirectAfterAuth()

  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [code, setCode] = useState('')

  const disabled = code.length < 6

  const handleOnChange = (code: string) => {
    setCode(code)
  }

  const handleSubmit = () => {
    if (disabled || loading) {
      return
    }

    setError(false)
    setLoading(true)

    API.Auth.verifyEmail({
      code,
    })
      .then(() => {
        dispatch(RequestActions.fetchAuth())
        sendGtmEvent(GtmTriggerName.AUTH_EMAIL_VERIFIED)
      })
      .catch((error) => {
        setLoading(false)
        setError(true)
        sendGtmEvent(
          GtmTriggerName.AUTH_OTP_ERROR,
          'Incorrect verification code'
        )
        console.error('Problem with verifying email', error)
      })
  }

  const onClickBack = () => {
    // @ts-expect-error
    dispatch(openModal(MODAL_AUTH, { authType: 'signup' }))
    dispatch({ type: ActionTypes.RESET_AUTH })
    pushPlanTier(null)
    clearRedirect()
  }

  return (
    <div className={css.verify_container}>
      <div className={css.verify_backWrap}>
        <div className={css.verify_back} onClick={onClickBack}>
          <IconLeft size={16} />
          <div>Back</div>
        </div>
      </div>
      <div className={css.verify_header}>
        <OctopusPic />
      </div>
      <div className={css.verify_title}>Check your email</div>
      <div className={css.verify_text}>
        We’ve sent a verification code to your email. Please check your inbox at{' '}
        <strong>{email}</strong>
      </div>
      <div className={css.verify_code}>
        <CodeInput
          code={code}
          error={error}
          disabled={loading}
          onChange={handleOnChange}
        />
      </div>
      {error && (
        <div className={css.verify_error}>Incorrect verification code</div>
      )}
      <div className={css.code_resend}>
        <ResendCode utcTime={emailVerificationCodeExpiresAt} />
      </div>
      <Button
        className={css.verify_button}
        label="Continue"
        loading={loading}
        disabled={disabled}
        onClick={handleSubmit}
      />
    </div>
  )
}
